<template>
  <view-wrapper name="Articles" :loading="loading" :loadingText="loadingText">
    <router-link
      class="mt-3 button big primary white"
      :to="{ path: '/articles/new' }"
      >Create new article
    </router-link>
    <div class="flex items-center justify-end">
      <div class="w-64 mr-4" v-if="connectors && connectors.length > 0">
        <drop-down
          :options="connectors"
          :value="activeConnector"
          @input="
            (e) => {
              activeConnector = e;
            }
          "
          value-property="ipAddress"
          searchProp="ipAddress"
          placeholder="Select connector"
        />
      </div>
      <span class="mr-2">Show Archived</span>
      <Checkbox :value="includeArchived" @input="onToggleArchived" />
    </div>
    <modal
      v-if="showWarning"
      @close="showWarning = false"
      type="warning"
      heading="Warning"
      :description="warningText"
    />
    <ExpandabelTable
      class="mt-5"
      :columnsToNotSort="columnsToNotSort"
      :headers="headers"
      :data="getArticles"
      @searchQuery="onSearch"
    >
      <template v-slot:child="{ child }">
        <div class="flex">
          <div class="flex flex-auto" style="text-align: right">
            <button
              v-if="getEditStatus(child)"
              class="button small"
              @click="save(child)"
            >
              Save <i style="font-size: 2em" class="edit-symbol" />
            </button>
            <button v-else class="button small" @click="edit(child)">
              Edit <i style="font-size: 2em" class="edit-symbol" />
            </button>
            <button class="button small" @click="deleteArticle(child)">
              Delete
            </button>
          </div>
          <div class="flex">
            <p>Archive:</p>
            <Toggle
              class="flex self-center ml-1"
              @click="toggleArchived(child)"
              :checked="child.archived"
            />
          </div>
        </div>
        <div v-for="(label, index) in child.labels" :key="label.name + index">
          <p>{{ label.name }}</p>
          <table class="subtable">
            <tbody>
              <tr v-for="(labelChild, index) in label.children" :key="index">
                <td>{{ labelChild.name }}</td>
                <td style="text-align: right">
                  <grafokett-input
                    v-if="getEditStatus(child)"
                    :maxlength="labelChild.size"
                    class="inputb"
                    v-model="labelChild.value"
                  />
                  <span v-else>{{ labelChild.value }}</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <p>Pending orders</p>
        <table class="subtable">
          <tbody>
            <tr
              v-for="(order, index) in child.orders"
              :key="order.orderId + index"
            >
              <td>{{ order.orderNumber }}</td>
            </tr>
          </tbody>
        </table>
      </template>
    </ExpandabelTable>
    <div class="py-2">
      <div class="float-right">
        <i
          class="p-1 mx-1 mt-1 cursor-pointer arrow left"
          @click="setPage(page - 1)"
        />
        <span class="select-none">Page {{ page }}</span>
        <i
          class="p-1 mx-1 mt-1 cursor-pointer arrow right"
          @click="setPage(page + 1)"
        />
      </div>
    </div>

    <AuthorizationZone
      allowAny="AccountType:Administrator,UserRole:ArticleManager"
    >
      <wrapper class="flex items-center mt-16">
        <drop-down
          v-if="importConfigurations && importConfigurations.length > 0"
          placeholder="Import Configuration"
          value-property="name"
          :options="importConfigurations"
          v-model="importConfiguration"
          class="flex-1"
        ></drop-down>
        <p
          v-if="importConfigurations && importConfigurations.length < 1"
          class="w-full"
        >
          You dont have any import configurations that allows article import
        </p>

        <grafokett-button
          class="flex-none ml-8 button primary"
          :disabled="!importConfiguration"
          @click="exportArticles"
        >
          Export
        </grafokett-button>
      </wrapper>
    </AuthorizationZone>
  </view-wrapper>
</template>

<script>
import { BehaviorSubject, combineLatest } from "rxjs";
import { debounceTime, distinctUntilChanged, mapTo } from "rxjs/operators";

import GrafokettInput from "../components/forms/Input.vue";
import ExpandabelTable from "../components/ExpandabelTable";
import { formatDate } from "../utils/dateHelpers";
import DropDown from "../components/forms/DropDown.vue";
import Checkbox from "../components/forms/Checkbox.vue";
import Toggle from "components/forms/Toggle";
import Modal from "components/Modal";
import AuthorizationZone from "../components/AuthorizationZone.vue";
import GrafokettButton from "../components/forms/Button.vue";

export default {
  components: {
    GrafokettInput,
    ExpandabelTable,
    DropDown,
    Checkbox,
    Toggle,
    Modal,
    AuthorizationZone,
    GrafokettButton,
  },
  data() {
    return {
      headers: {
        name: "Article name",
        modified: "Last edited",
        archived: "Archived",
      },
      showWarning: false,
      warningText: "",
      columnsToNotSort: [],
      loading: false,
      loadingText: "List of articles",
      page: 1,
      pageSize: 10,
      searchQuery: null,
      includeArchived: false,
      pageNumber$: null,
      pageSize$: null,
      searchQuery$: null,
      deletedArticle$: null,
      articles: [],
      connectors: [],
      activeConnector: null,
      importConfigurations: [],
      importConfiguration: null,
    };
  },
  methods: {
    async loadArticles(searchQuery, includeArchived, page, pageSize) {
      this.loading = true;
      const settingsResponse = await this.$store.$api.get("/api/subscription");

      if(settingsResponse.data.data.activeConnector && settingsResponse.data.data.activeConnector.id){
        this.activeConnector = this.connectors.find(x => x.id == settingsResponse.data.data.activeConnector.id);
      }

      let response = null;
      if(this.activeConnector && this.activeConnector.id) {
        response = await this.$store.$api.get(
          `/api/connectors/${this.activeConnector.id}/articles?take=1000&offset=0`
        );
      } else {
        response = await this.$store.$api.get(
          `/api/articles?take=${pageSize}&offset=${pageSize *
            (page -
              1)}&searchQuery=${searchQuery}&includeArchived=${includeArchived}`
        );
      }
      this.articles = response.data.data;
      this.loading = false;
    },
    async deleteArticle(child) {
      this.loading = true;
      const { data } = await this.$store.$api.get(
        `/api/articles/${child.parentId}/usage`
      );
      if (!data.data.length > 0) {
        this.$store
          .dispatch("ARTICLES/DELETE_ARTICLE", child.parentId)
          .then(() => {
            this.deletedArticle$.next(child.parentId);
          })
          .catch(err => console.log(err));
      } else {
        this.loading = false;
        this.warningText =
          "This article can't be removed because it has been printed.";
        this.showWarning = true;
      }
    },
    async activateConnector(connectorId){
      this.loadingText = 'Activating the connector';
      this.loading = true;
      this.$store.$api.put(`/api/connectors/${connectorId}/activate`, {
        }
      );
      this.loading = false;
    },
    async deactivateConnectors(){
      this.loadingText = 'Deactivating connector';
      this.loading = true;
      this.$store.$api.put(`/api/connectors/deactivate`, {
        }
      );
      this.loading = false;
    },
    async toggleArchived(article) {
      this.loading = true;
      await this.$store.$api.put(`/api/articles/archive/`, {
        articleId: article.parentId,
      });
      this.loading = false;
    },
    edit(child) {
      this.$router.push(`articles/${child.parentId}`);
    },
    save(child) {
      const article = this.getArticles.filter(e => e.id === child.parentId)[0];
      if (article !== undefined) {
        var payload = {
          articleId: article.id,
          variables: [],
        };
        article.expandableData.labels.forEach(l => {
          l.children.forEach(v => {
            var variable = {
              variableId: v.id,
              variableData: v.value,
            };
            payload.variables.push(variable);
          });
        });
        this.$store
          .dispatch("DATA/ADD_ARTICLE_VARIABLE_DATA", payload)
          .catch(err => console.log(err));
        article.edit = !article.edit;
      }
    },
    getEditStatus(child) {
      const article = this.getArticles.filter(e => e.id === child.parentId)[0];
      if (article !== undefined) {
        return article.edit;
      }
    },
    setPage(newPage) {
      this.pageNumber$.next(Math.max(1, newPage));
    },
    onSearch(value) {
      this.searchQuery$.next(value);
    },
    onToggleArchived(value) {
      this.includeArchived = !this.includeArchived;
      this.includeArchived$.next(value);
    },
    async exportArticles() {
      this.loading = true;
      const payload = {
        fileEncoding: this.fileEncoding && this.fileEncoding.name,
        importConfigurationId: this.importConfiguration.id,
      };

      const res = await this.$store.$api.post("/api/export/articles", payload, {
        responseType: "blob",
      });

      const a = document.createElement("a");
      a.href = window.URL.createObjectURL(res.data);
      a.download = `Export_${new Date().toISOString().substr(0, 10)}`;
      a.click();
      a.remove();
      this.loading = false;
    },
  },
  async mounted() {
    this.loading = true;

    const connectorData = await this.$store.$api.get(`/api/connectors?take=200&offset=0`);
    this.connectors = connectorData.data.data;

    this.pageNumber$ = new BehaviorSubject(1);
    this.pageNumber$$ = this.pageNumber$.subscribe(
      value => (this.page = value)
    );

    this.searchQuery$ = new BehaviorSubject("");
    this.includeArchived$ = new BehaviorSubject(false);
    this.searchQuery$$ = this.searchQuery$
      .pipe(
        distinctUntilChanged(),
        mapTo(1)
      )
      .subscribe(value => {
        this.pageNumber$.next(value);
      });
    this.pageSize$ = new BehaviorSubject(10);
    this.deletedArticle$ = new BehaviorSubject(0);

    this.searchStream$$ = combineLatest([
      this.searchQuery$.pipe(distinctUntilChanged()),
      this.includeArchived$.pipe(),
      this.pageNumber$.pipe(
        debounceTime(300),
        distinctUntilChanged()
      ),
      this.pageSize$,
      this.deletedArticle$,
    ])
      .pipe(debounceTime(300))
      .subscribe(([searchQuery, includeArchived, pageNumber, pageSize]) => {
        this.loadArticles(
          encodeURIComponent(searchQuery || ""),
          includeArchived,
          pageNumber,
          pageSize
        );
      });

    const { data } = await this.$store.$api.get("/api/importconfigurations");

    this.importConfigurations =
      data && data.data && data.data.filter(x => !x.usesOrders);

    this.importConfiguration = this.importConfigurations.find(x => x.default);
    this.loading = false;
  },
  beforeDestroy() {
    this.pageNumber$$.unsubscribe();
    this.searchQuery$$.unsubscribe();
    this.searchStream$$.unsubscribe();
  },
  watch: {
    activeConnector() {
      if(this.activeConnector.id) {
        this.activateConnector(this.activeConnector.id);
      }
    else {
      this.searchQuery = '';
      this.deactivateConnectors(this.activeConnector.id);
    }
      this.loadArticles(this.searchQuery, this.includeArchived, this.page, this.pageSize);
    },
    isAddButtonEnabled(newValue) {
      this.disabledFlag = !newValue;
    },
  },
  computed: {
    getArticles() {
      return this.articles.map(a => {
        return {
          id: a.id,
          name: a.name,
          archived: a.archived,
          modified: formatDate(a.modified),
          expandableData: {
            ...a,
          },
        };
      });
    },
  },
};
</script>
